import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Eye, Play, SkipBack, SkipForward, X } from 'lucide-react';
import {
  BEERPONG_CENTER_Y,
  BEERPONG_CUP_HEIGHT,
  BEERPONG_RACK_SIZE,
  BEERPONG_THROW_X,
  beerPongCupWorld,
  beerPongLanding,
  beerPongRack,
  deriveBeerPong,
  describeThrow,
  mirrorPoint,
  type BeerPongBallPoint,
  type BeerPongMove,
  type BeerPongPoint,
  type GameMeta,
} from '@awwyeah/shared';
import { useCurrentPlayer } from '../guestPlayer';
import { createGameMeta, nowIso } from '../gameOps';
import { useBeerPongMoves } from '../useBeerPongMoves';
import { SideLine } from '../components/SideLine';
import { SoundToggle } from '../components/SoundToggle';
import { unlockGameAudio } from '../synth';
import { playBounce, playRim, playSplash, playToss } from './sound';
import { useGamesRegistry } from '../useGamesRegistry';
import { useGameRoster } from '../useHouseholdRoster';
import { aimFromDrag, playThrow, replayThrow } from './playback';
import type { BeerPongFrame } from './physics';
import {
  beerPongLayout,
  drawTable,
  fitCanvasToDpr,
  toTable,
  toViewFrame,
  type BeerPongCupView,
  type BeerPongLayout,
  type BeerPongSplash,
} from './render';
import { publishRematch } from '../rematch';

/** What a throw did, for the moment it lands — sound, splash and the little flash line. */
interface ThrowMoment {
  defender: 0 | 1;
  cup: number | null;
  rimmed: number;
  /** Defender's cups left after this throw. */
  left: number;
}

const SPLASH_MS = 650;
const TRAIL_LENGTH = 7;

/**
 * Beer Pong board (WS-17f) — the second physics game, and deliberately the same shape as
 * Pool's: you take a whole throw, it lands in the move log as one entry, and the other
 * player's device plays exactly that throw back when they open it. Async by design; a family
 * plays this across a Tuesday, not in a sitting.
 *
 * **Where the truth lives.** Nothing on this screen is authoritative. The canvas draws
 * whatever `deriveBeerPong(moves)` says (`packages/shared/src/modules/games/beerpong.ts`),
 * the animation is a local re-simulation, and when a re-simulation disagrees with the
 * thrower's recorded snapshot the snapshot wins and a warning is logged (`playback.ts`'s
 * `replayThrow`). A spectator, a player mid-replay and a phone that just came back online all
 * converge on the same racks because they all derive them the same way.
 *
 * **The gesture**, borrowed wholesale from Pool because it's the right one and because two
 * physics games that aim differently would be two games to learn: drag *back* from your end
 * of the table and release. The direction is the line from your finger through the throwing
 * spot; the distance sets how far the ball carries, shown as a landing reticle that sits
 * exactly where the ball will come down through cup-rim height. The reticle is honest about
 * arrival and says nothing about whether it goes in — the capture window is about half a
 * cup wide, so aiming at a cup and *hitting* it are still different things.
 *
 * **Which way the table faces.** Always the viewer's own end at the bottom, for the whole
 * game — never a flip mid-match. The engine works in whichever player's frame a thing belongs
 * to (one simulator, one rulebook, both ends of the table); `toViewFrame` is the single place
 * that reconciles that with what's on screen.
 *
 * **Theme.** The table and the cups are the object, fixed in every theme (`render.ts`
 * explains why); every piece of chrome around them is semantic tokens. The only theme-derived
 * thing on the table is the current thrower's member colour, on the reticle and power arc.
 */

/** How far you drag for full power, in table units — about a quarter of the table, which is
 * a comfortable thumb travel on a phone and long enough that the aim is genuinely adjustable. */
const FULL_POWER_DRAG = 1;
/** Release this close to the throwing spot and nothing happens. */
const DEAD_ZONE = 0.12;
/** Below this the cups are too small to aim at, so the page scrolls instead of shrinking the
 * table further. Portrait is the orientation this game is designed for. */
const MIN_BOARD_HEIGHT = 420;

interface AimState {
  aimX: number;
  aimY: number;
  power: number;
  cancel: boolean;
  pullTo: BeerPongPoint;
}

const RACK = beerPongRack();

/** Where a given player throws from, in that player's own frame — a constant, and the same
 * one the simulator launches from. */
const THROW_SPOT: BeerPongPoint = { x: BEERPONG_THROW_X, y: BEERPONG_CENTER_Y };

/** One player's rack, as a count and a row of pips. A fact about what's left, never a score
 * and never a tally across games (`memory/no-demand-mechanics.md`). */
function RackStrip({
  label,
  color,
  standing,
  active,
  testId,
}: {
  label: string;
  color: string;
  standing: readonly number[];
  active: boolean;
  testId: string;
}): JSX.Element {
  return (
    <div
      className="flex min-h-[36px] items-center gap-2 rounded-md border border-line bg-surface px-2 py-1.5"
      data-testid={testId}
      data-active={active}
      data-standing={standing.length}
      style={active ? { borderColor: color } : undefined}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
        <span className="truncate text-xs font-semibold text-fg">{label}</span>
      </span>
      <span className="flex flex-1 flex-wrap items-center gap-1" aria-hidden="true">
        {Array.from({ length: BEERPONG_RACK_SIZE }, (_, cup) => (
          <span
            key={cup}
            data-testid="beerpong-pip"
            data-standing={standing.includes(cup)}
            className="h-2.5 w-2.5 rounded-full border"
            style={
              standing.includes(cup)
                ? { backgroundColor: '#cf3a34', borderColor: '#a82b26' }
                : { backgroundColor: 'transparent', borderColor: 'var(--line)' }
            }
          />
        ))}
      </span>
      <span className="shrink-0 text-[11px] text-fg-3">
        {standing.length} cup{standing.length === 1 ? '' : 's'}
      </span>
    </div>
  );
}

export function BeerPongBoardPage({ meta }: { meta: GameMeta }): JSX.Element {
  const navigate = useNavigate();
  // `useCurrentPlayer`, not `useUser`: on `/play/:token` the player is a guest with no
  // household session, and this board reads identity itself rather than taking it as a prop.
  const user = useCurrentPlayer();
  const roster = useGameRoster(meta.guestNames);
  const registries = useGamesRegistry();
  const { movesArray, moves, state } = useBeerPongMoves(meta.id, meta.playerIds);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const layoutRef = useRef<BeerPongLayout | null>(null);

  /** The ball being animated right now — a ref, not state, so a 60 Hz playback never
   * re-renders React; it just repaints the canvas. */
  const animBallRef = useRef<BeerPongBallPoint | null>(null);
  const playbackRef = useRef<{ cancel: () => void } | null>(null);
  /** How many moves have already been shown as an animation. `null` until the move log has
   * loaded, so opening an old game doesn't replay the whole match at you. */
  const animatedThroughRef = useRef<number | null>(null);
  const draggingRef = useRef(false);
  /** The cup a throw in flight is about to sink: the derived racks already have it gone, but
   * it should stay standing until the ball actually gets there. */
  const holdCupRef = useRef<{ side: 0 | 1; cup: number } | null>(null);
  const trailRef = useRef<BeerPongBallPoint[]>([]);
  const splashRef = useRef<BeerPongSplash | null>(null);
  const splashRafRef = useRef(0);
  const [flash, setFlash] = useState<string | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [animating, setAnimating] = useState(false);
  const [aim, setAim] = useState<AimState | null>(null);
  const [replayIndex, setReplayIndex] = useState<number | null>(null);
  const [diverged, setDiverged] = useState(false);
  const [boardHeight, setBoardHeight] = useState(420);

  const isPlayer = user ? meta.playerIds.includes(user.id) : false;
  const meIndex = user ? meta.playerIds.indexOf(user.id) : -1;
  /** Whose end of the table is at the bottom of the screen. A spectator watches from player
   * 0's end — an arbitrary choice, but a *stable* one, which is the property that matters. */
  const viewSide: 0 | 1 = meIndex === 1 ? 1 : 0;

  /** What the canvas is showing: a replayed prefix while scrubbing, otherwise live. */
  const shownState = useMemo(
    () =>
      replayIndex === null ? state : deriveBeerPong(moves.slice(0, replayIndex), meta.playerIds),
    [replayIndex, state, moves, meta.playerIds],
  );

  const finished = shownState.winner !== null || state.winner !== null;
  const myTurn = isPlayer && state.turn !== null && state.turn === meIndex && !finished;
  const throwerIndex: 0 | 1 = state.turn ?? state.winner ?? 0;
  const turnColor = roster.getMember(meta.playerIds[throwerIndex] ?? '').color;

  /** A point in some player's own frame → the frame the canvas draws in. */
  const toView = useCallback(
    <T extends BeerPongPoint>(p: T, ownerSide: 0 | 1): T => toViewFrame(p, ownerSide !== viewSide),
    [viewSide],
  );

  const cups: BeerPongCupView[] = useMemo(() => {
    const out: BeerPongCupView[] = [];
    for (const side of [0, 1] as const) {
      for (let cup = 0; cup < BEERPONG_RACK_SIZE; cup += 1) {
        const world = beerPongCupWorld(side, cup, RACK);
        out.push({
          at: viewSide === 1 ? mirrorPoint(world) : world,
          standing: shownState.standing[side].includes(cup),
        });
      }
    }
    return out;
  }, [shownState, viewSide]);

  const throwSpotView = useMemo(() => toView(THROW_SPOT, throwerIndex), [toView, throwerIndex]);

  // ---- painting -----------------------------------------------------------------------
  const paint = useCallback((): void => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const cssWidth = wrap.clientWidth;
    const cssHeight = wrap.clientHeight;
    if (cssWidth < 2 || cssHeight < 2) return;
    const ctx = fitCanvasToDpr(canvas, cssWidth, cssHeight);
    if (!ctx) return;
    const layout = beerPongLayout(cssWidth, cssHeight);
    layoutRef.current = layout;

    const ball = animBallRef.current;
    const hold = holdCupRef.current;
    const sceneCups = hold
      ? cups.map((c, i) =>
          i === hold.side * BEERPONG_RACK_SIZE + hold.cup ? { ...c, standing: true } : c,
        )
      : cups;
    drawTable(ctx, layout, {
      cups: sceneCups,
      turnColor,
      ...(ball ? { ball, trail: trailRef.current } : {}),
      ...(splashRef.current ? { splash: splashRef.current } : {}),
      ...(ball || finished || replayIndex !== null ? {} : { restingBall: throwSpotView }),
      ...(aim && !ball
        ? {
            aim: {
              from: throwSpotView,
              // The reticle is computed in the thrower's frame (that's where the maths
              // lives) and brought back to the view frame like everything else.
              to: toView(beerPongLanding(aim.aimX, aim.aimY, aim.power), throwerIndex),
              power: aim.power,
              pullTo: aim.pullTo,
              cancel: aim.cancel,
            },
          }
        : {}),
    });
  }, [cups, turnColor, aim, throwSpotView, throwerIndex, toView, finished, replayIndex]);

  useLayoutEffect(() => {
    paint();
  }, [paint]);

  // Board size: as tall as the space actually left below it. Measured rather than guessed at
  // from a viewport fraction — the app header, the PWA install prompt and the bottom nav all
  // move the board's top edge around, and a fixed fraction pushes the near end (the end you
  // throw from) off screen exactly when it matters.
  useEffect(() => {
    const measure = (): void => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const width = wrap.clientWidth;
      const top = wrap.getBoundingClientRect().top;
      const viewport = typeof window === 'undefined' ? 720 : window.innerHeight;
      // Reserve for the near player's rack strip, the hint line and the bottom nav.
      const available = viewport - top - 150;
      setBoardHeight(Math.round(Math.max(MIN_BOARD_HEIGHT, Math.min(width * 3.12, available))));
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (wrapRef.current) observer.observe(wrapRef.current);
    // Things above the board come and go (the open-seat invite panel, the install prompt);
    // watching the page catches those, which the board's own box never notices.
    observer.observe(document.body);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, []);

  useEffect(() => {
    paint();
  }, [boardHeight, paint]);

  // ---- throw animation ----------------------------------------------------------------
  const showFlash = useCallback((text: string): void => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setFlash(text);
    flashTimerRef.current = setTimeout(() => setFlash(null), 1500);
  }, []);

  /** The ball has arrived: let the held cup go, splash the beer, say what happened. */
  const land = useCallback(
    (moment: ThrowMoment | null): void => {
      cancelAnimationFrame(splashRafRef.current);
      splashRef.current = null;
      if (!moment) return;
      if (moment.cup === null) {
        if (moment.rimmed > 0) showFlash('So close — rattled out');
        return;
      }
      const world = beerPongCupWorld(moment.defender, moment.cup, RACK);
      const at = viewSide === 1 ? mirrorPoint(world) : world;
      playSplash(moment.left === 0);
      showFlash(
        moment.left === 0
          ? 'Last cup — game!'
          : moment.left === 1
            ? 'Splash! One cup left'
            : moment.rimmed > 0
              ? 'Rattled in!'
              : 'Splash!',
      );
      const started = performance.now();
      const step = (now: number): void => {
        const t = (now - started) / SPLASH_MS;
        splashRef.current = t >= 1 ? null : { at, t };
        paint();
        if (t < 1) splashRafRef.current = requestAnimationFrame(step);
      };
      splashRafRef.current = requestAnimationFrame(step);
    },
    [paint, showFlash, viewSide],
  );

  const runPlayback = useCallback(
    (frames: readonly BeerPongFrame[], ownerSide: 0 | 1, moment: ThrowMoment | null): void => {
      playbackRef.current?.cancel();
      holdCupRef.current =
        moment && moment.cup !== null ? { side: moment.defender, cup: moment.cup } : null;
      trailRef.current = [];
      setAnimating(true);
      playToss();
      let prev: BeerPongBallPoint | null = null;
      let prevVz = 0;
      playbackRef.current = playThrow(
        frames,
        (ball) => {
          // A falling ball that starts rising again just bounced: off a rim if it was up at
          // cup height, off the table if it was down low. Sound only — display, not physics.
          if (prev) {
            const vz = ball.z - prev.z;
            if (prevVz < -1e-4 && vz > 1e-4) {
              if (prev.z > BEERPONG_CUP_HEIGHT * 0.6) playRim();
              else playBounce(Math.min(1, -prevVz * 25));
            }
            if (Math.abs(vz) > 1e-6) prevVz = vz;
          }
          prev = ball;
          const view = toView(ball, ownerSide);
          const last = animBallRef.current;
          if (last) trailRef.current = [...trailRef.current, last].slice(-TRAIL_LENGTH);
          animBallRef.current = view;
          paint();
        },
        () => {
          animBallRef.current = null;
          playbackRef.current = null;
          holdCupRef.current = null;
          trailRef.current = [];
          setAnimating(false);
          land(moment);
          paint();
        },
      );
    },
    [paint, toView, land],
  );

  /** Re-simulate move `index` from the log and animate it — the same code path for "the
   * opponent's throw just arrived", "I opened the game and hadn't seen it yet" and "watch
   * the replay". */
  const animateMove = useCallback(
    (index: number): void => {
      const move = moves[index];
      if (!move) return;
      const before = deriveBeerPong(moves.slice(0, index), meta.playerIds);
      const thrower = before.turn;
      if (thrower === null) return;
      const defender: 0 | 1 = thrower === 0 ? 1 : 0;
      const sim = replayThrow(
        before.standing[defender],
        { aimX: move.aimX, aimY: move.aimY, power: move.power },
        move.outcome,
      );
      if (sim.diverged) setDiverged(true);
      const left = before.standing[defender].length - (sim.outcome.cup !== null ? 1 : 0);
      runPlayback(sim.frames, thrower, {
        defender,
        cup: sim.outcome.cup,
        rimmed: sim.outcome.rimmed.length,
        left,
      });
    },
    [moves, meta.playerIds, runPlayback],
  );

  // New throws arriving (mine are animated at throw time; this catches the opponent's, and
  // the "I opened the game and their throw is waiting" case).
  useEffect(() => {
    if (replayIndex !== null) return;
    if (animatedThroughRef.current === null) {
      if (moves.length === 0) return;
      const last = moves[moves.length - 1];
      animatedThroughRef.current = last?.throwerId === user?.id ? moves.length : moves.length - 1;
    }
    if (animatedThroughRef.current >= moves.length) {
      animatedThroughRef.current = moves.length;
      return;
    }
    const index = moves.length - 1; // if several arrived at once, show the latest
    animatedThroughRef.current = moves.length;
    animateMove(index);
  }, [moves, user?.id, replayIndex, animateMove]);

  useEffect(
    () => () => {
      playbackRef.current?.cancel();
      cancelAnimationFrame(splashRafRef.current);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    },
    [],
  );

  // ---- the gesture --------------------------------------------------------------------
  const pointerToTable = (event: React.PointerEvent<HTMLCanvasElement>): BeerPongPoint | null => {
    const canvas = canvasRef.current;
    const layout = layoutRef.current;
    if (!canvas || !layout) return null;
    const rect = canvas.getBoundingClientRect();
    return toTable(layout, event.clientX - rect.left, event.clientY - rect.top);
  };

  /** While it's your throw, the view frame *is* your frame — the mirror only ever applies to
   * the other end of the table — so a drag needs no conversion at all. */
  const updateAim = (at: BeerPongPoint): void => {
    setAim({ ...aimFromDrag(THROW_SPOT, at, FULL_POWER_DRAG, DEAD_ZONE), pullTo: at });
  };

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    unlockGameAudio();
    if (!myTurn || animating || replayIndex !== null) return;
    const at = pointerToTable(event);
    if (!at) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    draggingRef.current = true;
    updateAim(at);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!draggingRef.current || !myTurn || animating) return;
    const at = pointerToTable(event);
    if (!at) return;
    updateAim(at);
  };

  const release = (aimed: AimState): void => {
    if (!user || state.turn === null) return;
    const defender: 0 | 1 = state.turn === 0 ? 1 : 0;
    const sim = replayThrow(state.standing[defender], aimed);
    const move: BeerPongMove = {
      throwerId: user.id,
      aimX: aimed.aimX,
      aimY: aimed.aimY,
      power: aimed.power,
      outcome: sim.outcome,
      at: nowIso(),
    };
    movesArray.push([move]);
    animatedThroughRef.current = movesArray.length;
    runPlayback(sim.frames, state.turn, {
      defender,
      cup: sim.outcome.cup,
      rimmed: sim.outcome.rimmed.length,
      left: state.standing[defender].length - (sim.outcome.cup !== null ? 1 : 0),
    });
  };

  const onPointerUp = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const current = aim;
    const wasDragging = draggingRef.current;
    draggingRef.current = false;
    setAim(null);
    if (!wasDragging || !current || !myTurn || animating) return;
    if (current.cancel) return; // released in the dead zone — changed your mind
    release(current);
  };

  // ---- finishing ----------------------------------------------------------------------
  // Mirror the decided game onto the registry so the Apps tab's quiet Finished section picks
  // it up. Players only — a spectator's client never writes anything — and never a guest:
  // `games` is the one doc a guest may never open (WS-23), so a guest winner leaves the
  // stamp to the household member's own client.
  useEffect(() => {
    if (!user || !isPlayer || user.isGuest) return;
    if (meta.status === 'done' || state.winner === null) return;
    const at = nowIso();
    registries.map.set(meta.id, {
      ...meta,
      status: 'done',
      winnerId: meta.playerIds[state.winner],
      finishedAt: at,
      updatedBy: user.id,
      updatedAt: at,
    });
  }, [meta, user, isPlayer, registries.map, state.winner]);

  const rematch = (): void => {
    if (!user) return;
    const opponentId = meta.playerIds.find((id) => id !== user.id) ?? meta.playerIds[1] ?? '';
    const fresh = createGameMeta({
      kind: meta.kind,
      starterId: user.id,
      starterName: roster.getMember(user.id).name,
      opponentId,
      opponentName: roster.getMember(opponentId).name,
    });
    publishRematch(registries.map, fresh, meta);
    navigate(`/games/${fresh.id}`);
  };

  // ---- replay -------------------------------------------------------------------------
  const stopReplay = (): void => {
    playbackRef.current?.cancel();
    holdCupRef.current = null;
    animBallRef.current = null;
    setAnimating(false);
    setReplayIndex(null);
  };
  const showThrow = (index: number): void => {
    if (index < 0 || index >= moves.length) return;
    // The board sits on the *post*-throw racks; the animation on top of it is what shows the
    // throw happening, and when it ends the underlying state is already the right one.
    setReplayIndex(index + 1);
    animateMove(index);
  };

  if (!user) return <></>;

  const opponentId = meta.playerIds.find((id) => id !== user.id) ?? meta.playerIds[1] ?? '';
  const opponent = roster.getMember(opponentId);
  const nearSide: 0 | 1 = viewSide;
  const farSide: 0 | 1 = viewSide === 0 ? 1 : 0;
  const nearPlayer = roster.getMember(meta.playerIds[nearSide] ?? '');
  const farPlayer = roster.getMember(meta.playerIds[farSide] ?? '');

  const statusLine = (): string => {
    if (state.winner !== null) {
      const winnerId = meta.playerIds[state.winner];
      if (!isPlayer) return `${roster.getMember(winnerId ?? '').name} won`;
      return winnerId === user.id ? 'You won' : `${opponent.name} won`;
    }
    if (state.turn === null) return '';
    const turnId = meta.playerIds[state.turn];
    if (!isPlayer) return `${roster.getMember(turnId ?? '').name} to throw`;
    return turnId === user.id ? 'Your throw' : `${opponent.name}'s throw`;
  };

  const hint = (): string => {
    if (finished) return '';
    if (!myTurn) return 'Waiting on their throw.';
    return 'Drag back from your end, let go to throw.';
  };

  return (
    <div
      className="flex flex-col gap-3 py-4"
      data-testid="beerpong-board-page"
      data-game-kind="beerpong"
    >
      <style>{`@keyframes awwyeah-bp-flash { 0% { transform: translate(-50%,-50%) scale(.6); opacity: 0 } 15% { transform: translate(-50%,-50%) scale(1.08); opacity: 1 } 80% { opacity: 1 } 100% { transform: translate(-50%,-62%) scale(1); opacity: 0 } }
@media (prefers-reduced-motion: reduce) { [data-game-kind="beerpong"] * { animation: none !important } }`}</style>
      <header className="flex items-center gap-2">
        <button
          type="button"
          aria-label={user?.isGuest ? 'Back to the lounge' : 'Back to Games'}
          className="flex h-11 w-11 items-center justify-center text-fg-3"
          onClick={() => navigate(user?.isGuest ? '/play/lounge' : '/games')}
        >
          <ChevronLeft size={22} />
        </button>
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-display text-xl tracking-tight text-fg">{meta.title}</h2>
          <p
            className="text-sm text-fg-2"
            data-testid="beerpong-status-line"
            data-my-turn={myTurn}
            data-finished={finished}
          >
            {!isPlayer && (
              <span className="mr-1 inline-flex items-center gap-1 text-fg-3">
                <Eye size={13} aria-hidden="true" /> Spectating
              </span>
            )}
            {statusLine()}
          </p>
        </div>
        {finished && moves.length > 0 && replayIndex === null && (
          <button
            type="button"
            data-testid="beerpong-watch-replay"
            onClick={() => showThrow(0)}
            className="flex h-11 shrink-0 items-center gap-1.5 rounded-full border border-line-strong px-3 text-sm font-semibold text-fg"
          >
            <Play size={15} aria-hidden="true" /> Replay
          </button>
        )}
      </header>

      {isPlayer && (
        <SideLine
          color={roster.getMember(user.id).color}
          // Your end is always drawn at the bottom (`viewSide`), so that's where you throw from.
          detail="near end"
          turnText={statusLine()}
          turnColor={finished ? null : turnColor}
          myTurn={myTurn}
        />
      )}

      <RackStrip
        label={farPlayer.name}
        color={farPlayer.color}
        standing={shownState.standing[farSide]}
        active={state.turn === farSide && !finished}
        testId="beerpong-rack-far"
      />

      <div
        ref={wrapRef}
        className="relative mx-auto w-full overflow-hidden rounded-md"
        // Never wider than it is tall, so the table always stands up with your end at the
        // bottom — on a laptop too, not just a phone. Purely how it's drawn; `toTable` still
        // inverts `toCanvas` exactly, whatever the box.
        style={{ height: boardHeight, maxWidth: Math.round(boardHeight * 0.8) }}
        data-testid="beerpong-table-wrap"
      >
        <canvas
          ref={canvasRef}
          data-testid="beerpong-canvas"
          /* Test seam: the standing cups per side, so browser verification can aim at a real
             cup instead of guessing at pixels. Derived state only — it never updates during
             an animation, and nothing in the app reads it. */
          data-standing={JSON.stringify(shownState.standing)}
          className="absolute inset-0 touch-none"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
        {flash && (
          <div
            className="pointer-events-none absolute left-1/2 top-[42%] whitespace-nowrap rounded-full px-4 py-2 font-display text-lg font-semibold"
            style={{
              background: 'rgba(255,248,228,.96)',
              color: '#7d1712',
              boxShadow: '0 6px 18px rgba(0,0,0,.35)',
              animation: 'awwyeah-bp-flash 1500ms ease-out both',
              transform: 'translate(-50%,-50%)',
            }}
            data-testid="beerpong-flash"
            role="status"
          >
            {flash}
          </div>
        )}
      </div>

      <RackStrip
        label={nearPlayer.name}
        color={nearPlayer.color}
        standing={shownState.standing[nearSide]}
        active={state.turn === nearSide && !finished}
        testId="beerpong-rack-near"
      />

      {replayIndex === null ? (
        <>
          <div className="flex items-center gap-2">
            <p className="min-h-[20px] flex-1 text-sm text-fg-2" data-testid="beerpong-hint">
              {animating ? '' : hint()}
            </p>
            <SoundToggle />
          </div>
          {state.lastThrow && !animating && (
            <p className="text-xs text-fg-3" data-testid="beerpong-last-throw">
              {roster.getMember(meta.playerIds[state.lastThrow.thrower] ?? '').name}:{' '}
              {describeThrow(state.lastThrow)}
            </p>
          )}
        </>
      ) : (
        <div
          className="flex items-center gap-2 rounded-md border border-line bg-raised p-2"
          data-testid="beerpong-replay-controls"
        >
          <button
            type="button"
            aria-label="Previous throw"
            data-testid="beerpong-replay-back"
            disabled={replayIndex <= 1}
            onClick={() => showThrow(replayIndex - 2)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-fg disabled:opacity-40"
          >
            <SkipBack size={16} />
          </button>
          <span
            className="flex-1 text-center text-xs text-fg-2"
            data-testid="beerpong-replay-position"
          >
            Throw {replayIndex} of {moves.length}
          </span>
          <button
            type="button"
            aria-label="Next throw"
            data-testid="beerpong-replay-forward"
            disabled={replayIndex >= moves.length}
            onClick={() => showThrow(replayIndex)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-fg disabled:opacity-40"
          >
            <SkipForward size={16} />
          </button>
          <button
            type="button"
            aria-label="Exit replay"
            data-testid="beerpong-replay-exit"
            onClick={stopReplay}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-fg"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {diverged && (
        <p className="text-xs text-fg-3" data-testid="beerpong-diverged">
          This throw replayed slightly differently here — showing the thrower&apos;s result.
        </p>
      )}

      {finished && isPlayer && user !== null && !user.isGuest && replayIndex === null && (
        <button
          type="button"
          data-testid="rematch-button"
          onClick={rematch}
          className="h-12 rounded-md bg-inverse text-base font-semibold text-inverse-fg"
        >
          Rematch
        </button>
      )}
    </div>
  );
}
